import { config as defaultConfig } from '@gluestack-ui/config';

/**
 * Conbent design tokens for an enterprise workspace UI.
 *
 * The interactive blue is a notch deeper than the logo's `#3379D9` so links and primary
 * actions keep AA contrast on white and read as restrained rather than playful. Surfaces are
 * neutral cool greys (not blue-tinted) so dense BIM data, not the chrome, carries the colour.
 * Keep these values in step with the CSS custom properties in `index.css`; component dimensions live in `shared/styles/tokens.ts`.
 */
const CONBENT_PRIMARY = {
  primary0: '#F0F5FC',
  primary50: '#E1EBF8',
  primary100: '#C3D6F1',
  primary200: '#9BBCE6',
  primary300: '#6B9BD8',
  primary400: '#407DCB',
  primary500: '#1F63C6',
  primary600: '#1A54A8',
  primary700: '#15458A',
  primary800: '#10356B',
  primary900: '#0B254C',
  primary950: '#07172F',
} as const;

const CONBENT_SURFACES = {
  // Dark — neutral graphite rather than saturated navy, so status colours stay legible.
  backgroundDark950: '#0B0E13',
  backgroundDark900: '#11151C',
  backgroundDark800: '#171C24',
  backgroundDark700: '#1F2630',
  backgroundDark600: '#2C3440',
  borderDark700: '#262D38',
  borderDark600: '#333C49',
  textDark0: '#FFFFFF',
  textDark50: '#F4F6F8',
  textDark100: '#E8EBEF',
  textDark200: '#D9DEE5',
  textDark300: '#BCC4CF',
  textDark400: '#949FAE',
  textDark500: '#6E7988',
  textDark600: '#525C6A',
  textDark700: '#3A424E',
  textDark800: '#232A34',
  textDark900: '#141920',
  // Light — neutral canvas with crisp white working surfaces and hairline borders.
  backgroundLight50: '#F6F7F9',
  backgroundLight100: '#EEF0F3',
  backgroundLight0: '#FFFFFF',
  borderLight200: '#E3E7EC',
  borderLight300: '#D2D8E0',
  textLight900: '#141B26',
  textLight800: '#222B38',
  textLight700: '#3A4553',
  textLight600: '#5A6577',
  textLight500: '#848E9D',
  // Soft status fills used by `Badge`/`Alert`; muted so a column of statuses doesn't shout.
  backgroundLightError: '#FDF0F0',
  backgroundLightWarning: '#FDF5E9',
  backgroundLightSuccess: '#EDF7F1',
  backgroundLightInfo: '#EDF3FC',
  backgroundLightMuted: '#F1F3F5',
  backgroundDarkError: '#3A1A1C',
  backgroundDarkWarning: '#3A2A14',
  backgroundDarkSuccess: '#16301F',
  backgroundDarkInfo: '#152842',
  backgroundDarkMuted: '#232A34',
} as const;

const CONBENT_SUCCESS = {
  success400: '#4FB37A',
  success500: '#2E9A5E',
  success600: '#22804B',
  success700: '#1B663C',
} as const;

const CONBENT_FONT =
  "Inter, 'Segoe UI', system-ui, -apple-system, Roboto, 'Helvetica Neue', Arial, sans-serif";

export const conbentThemeConfig = {
  ...defaultConfig,
  tokens: {
    ...defaultConfig.tokens,
    colors: {
      ...defaultConfig.tokens.colors,
      ...CONBENT_PRIMARY,
      ...CONBENT_SURFACES,
      ...CONBENT_SUCCESS,
    },
    // Tighter corners across every Gluestack control: square-ish reads as tooling, round as consumer.
    radii: {
      ...defaultConfig.tokens.radii,
      md: 4,
      lg: 4,
      xl: 6,
      '2xl': 6,
      '3xl': 8,
    },
    // Compact type scale matching `--text-*` in index.css; the Gluestack defaults run one step larger.
    fontSizes: {
      ...defaultConfig.tokens.fontSizes,
      '2xs': 11,
      xs: 12,
      sm: 13,
      md: 14,
      lg: 16,
      xl: 18,
      '2xl': 20,
      '3xl': 24,
    },
    fonts: {
      ...defaultConfig.tokens.fonts,
      heading: CONBENT_FONT,
      body: CONBENT_FONT,
    },
    fontWeights: {
      ...defaultConfig.tokens.fontWeights,
      // `bold` / `Heading` resolve here; semibold keeps hierarchy without a heavy, shouty page.
      bold: '600',
    },
  },
} as unknown as typeof defaultConfig;
